import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { Button, Card, Col, DatePicker, Row, Space } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { getShopStatistics } from "@/api/shopStatistics";
import type { ShopStatisticsData } from "@/api/shopStatistics";
import styles from "@/static/css/index.module.css";

export default function ShopStatistics(): ReactElement {
  const [pickerValue, setPickerValue] = useState<[Dayjs, Dayjs]>(() => [dayjs(), dayjs()]);
  const [queryRange, setQueryRange] = useState(() => ({
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  }));
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<ShopStatisticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    void getShopStatistics(queryRange.startDate, queryRange.endDate, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setData(response.data);
      })
      .catch(() => {
        // 请求层统一提示错误，失败时不展示为零值统计。
        if (!controller.signal.aborted) setData(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return (): void => controller.abort();
  }, [queryRange, revision]);

  const handleDateChange = (value: [Dayjs | null, Dayjs | null] | null): void => {
    if (value?.[0] && value[1]) setPickerValue([value[0], value[1]]);
  };

  const handleQuery = (): void => {
    setQueryRange({
      startDate: pickerValue[0].format("YYYY-MM-DD"),
      endDate: pickerValue[1].format("YYYY-MM-DD"),
    });
    setRevision((value) => value + 1);
  };

  const handleReset = (): void => {
    const today = dayjs();
    setPickerValue([today, today]);
    setQueryRange({
      startDate: today.format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    });
    setRevision((value) => value + 1);
  };

  const groups = [
    {
      key: "selfOperated",
      metrics: [
        { title: "自营-购买订单量", value: data?.selfOperated?.paidOrderCount },
        { title: "自营-成交总金额", value: data?.selfOperated?.transactionAmount },
        { title: "自营-结算金额", value: data?.selfSettlementAmount },
        { title: "自营-平台分润", value: data?.selfPlatformCommissionAmount },
        { title: "自营-退款申请订单量", value: data?.selfOperated?.refundOrderCount },
      ],
    },
    {
      key: "merchant",
      metrics: [
        { title: "入驻-购买订单量", value: data?.merchant?.paidOrderCount },
        { title: "入驻-成交总金额", value: data?.merchant?.transactionAmount },
        { title: "入驻-结算金额", value: data?.merchantSettlementAmount },
        { title: "入驻-平台分润金额", value: data?.platformCommissionAmount },
        { title: "入驻-退款申请订单量", value: data?.merchant?.refundOrderCount },
      ],
    },
  ];

  return (
    <div className={styles.home} style={{ borderBottom: "none" }}>
      <div className={styles.home_body}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
          <div style={{ marginRight: 24, fontSize: 18, fontWeight: "bold", whiteSpace: "nowrap" }}>商城数据</div>
          <Space size={16} wrap>
            <DatePicker.RangePicker
              placeholder={["开始时间", "截止时间"]}
              value={pickerValue}
              format="YYYY-MM-DD"
              allowClear={false}
              onChange={handleDateChange}
            />
            <Space>
              <Button type="primary" onClick={handleQuery} loading={loading}>查询</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Space>
        </div>
        {groups.map((group) => (
          <Row key={group.key} gutter={[8, 16]} style={{ marginBottom: 16 }}>
            {group.metrics.map((metric) => (
              <Col xs={24} sm={12} lg={6} key={metric.title}>
                <Card bordered={false} loading={loading} style={{ height: "128px" }}>
                  <div className={styles.home_body_col}>
                    <span style={{ color: "#333" }}>{metric.title}</span>
                    <span className={styles.col_price}>{metric.value ?? "-"}</span>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ))}
      </div>
    </div>
  );
}
